import { Color, Spacing } from '@persona-id/design-tokens';
import {
  CaretDownIcon,
  EditBoxIcon,
  ListIcon,
  TrashIcon,
  XIcon,
} from '@persona-id/design-tokens/icons';
import { isEqual, omit } from 'lodash/fp';
import { ReactNode, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FieldPath, FieldPathValue } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { MediumHeaderContainer } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/shared';
import { MATCH_REQUIREMENT_CHECK_NAMES } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/utils';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { CheckStubsPanel } from 'dashboard/components/Simulate/CheckStubsPanel';
import { GovernmentIdDetailsTable } from 'dashboard/components/Simulate/GovernmentIdDetailsTable';
import { GovernmentIdDocumentStubsPanel } from 'dashboard/components/Simulate/GovernmentIdDocumentStubsPanel';
import { MatchRequirementCheckStubsPanel } from 'dashboard/components/Simulate/MatchRequirementCheckStubsPanel';
import { VerificationChecksTable } from 'dashboard/components/Simulate/VerificationChecksTable';
import { VerificationMatchRequirementChecksTable } from 'dashboard/components/Simulate/VerificationMatchRequirementChecksTable';
import { SaveAsPresetModal } from 'dashboard/components/SimulatePreset/SaveAsPresetModal';
import { UpdatePresetModal } from 'dashboard/components/SimulatePreset/UpdatePresetModal';
import { VerificationPresetsPanel } from 'dashboard/components/SimulatePreset/VerificationPresetsPanel';
import { vtvApiTypePresetToVerificationType } from 'dashboard/lib/simulate/presets/utils';
import {
  CreateSimulatePresetAttributes,
  SimulateCustomPreset,
  SimulatePresetType,
  StubsWithOptionalId,
  UpdateSimulatePresetAttributes,
  VERIFICATION_TYPE_TO_PRESETS,
} from 'dashboard/models/simulate/preset';
import {
  CheckStub,
  GovernmentIdExtractions,
  MatchRequirementsCheckStub,
  SandboxForcedStatus,
  VerificationTemplateVersionStubs,
} from 'dashboard/models/simulate/stubs';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import hoverableMixin from 'design-system/mixins/hoverable';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

const IframeOverlay = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: ${Z_INDICES.modal};
`;

const PresetHeaderContainer = styled.div`
  display: flex;
  gap: ${Spacing[2]};
`;

const PresetCardFlexGroup = styled.div`
  display: flex;
  gap: ${Spacing[3]};
  flex-direction: row;
  align-items: center;
`;

const PresetCardDescription = styled(Paragraph)`
  line-height: 22px;
  text-overflow: ellipsis;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const StubButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[1]};
  margin-bottom: ${Spacing[1]};
`;

const PresetCard = styled.div<{ selected?: boolean }>`
  display: flex;
  ${hoverableMixin};
  &:hover,
  &:active,
  &:focus {
    ${({ selected }) =>
      !selected &&
      css`
        outline: 2px solid ${({ theme }) => theme.color.primary.brand200};
      `}
  }
  flex: none;
  flex-direction: row;
  justify-content: space-between;
  gap: ${Spacing[3]};
  width: 100%;
  outline: 1px solid ${({ theme }) => theme.color.border.primary};
  border-radius: ${Spacing[2]};
  padding: 14px;
  cursor: pointer;
  background-color: ${Color.PrimaryWhite};
  align-items: center;
  ${({ selected }) =>
    selected &&
    css`
      outline: 1px solid ${({ theme }) => theme.color.primary.brand600};
    `}
`;

const portaledPanel = (component: ReactNode, panelPortalId?: string) => {
  if (panelPortalId) {
    return createPortal(component, document.getElementById(panelPortalId) as Element);
  }

  return component;
};

interface VerificationTemplateVersionStubsConfigProps {
  verificationTemplateVersion: VerificationTemplateVersion;
  verificationTemplateVersionStubs: VerificationTemplateVersionStubs;
  setVerificationTemplateVersionStubsFieldPath: (
    stubFieldPath: FieldPath<VerificationTemplateVersionStubs> | null,
    stub:
      | FieldPathValue<
          VerificationTemplateVersionStubs,
          FieldPath<VerificationTemplateVersionStubs>
        >
      | VerificationTemplateVersionStubs,
  ) => void;
  customPresets: SimulateCustomPreset[];
  loadingSimulatePresets: boolean;
  handleCreatePreset: (attrs: CreateSimulatePresetAttributes) => Promise<SimulateCustomPreset>;
  handleUpdatePreset: (
    id: string,
    type: SimulatePresetType,
    props: UpdateSimulatePresetAttributes,
  ) => Promise<SimulateCustomPreset>;
  reloadPresets: () => Promise<void>;
  selectedPreset: StubsWithOptionalId | null;
  setSelectedPreset: (preset: StubsWithOptionalId | null) => void;
  removeCustomPreset: (preset: StubsWithOptionalId) => void;
  // id of the element an overlay should be rendered when the panel is opened
  panelOverlayId?: string;
  // id of the parent element of the panel via portal
  panelPortalId?: string;
  // id of the element the panel should be offset by
  offsetElementId?: string;
  trackEvent: (eventName: string, props?: Record<string, any>) => void;
}

const VerificationTemplateVersionStubsConfig = ({
  verificationTemplateVersion,
  verificationTemplateVersionStubs,
  setVerificationTemplateVersionStubsFieldPath,
  customPresets,
  loadingSimulatePresets,
  handleCreatePreset,
  handleUpdatePreset,
  reloadPresets,
  selectedPreset,
  setSelectedPreset,
  removeCustomPreset,
  panelPortalId,
  panelOverlayId,
  offsetElementId,
  trackEvent,
}: VerificationTemplateVersionStubsConfigProps) => {
  const [
    hasOpenSaveAsPresetModal,
    { enable: showSaveAsPresetModal, disable: hideSaveAsPresetModal },
  ] = useToggle(false);
  const [
    hasOpenUpdatePresetModal,
    { enable: showUpdatePresetModal, disable: hideUpdatePresetModal },
  ] = useToggle(false);
  const [isCheckStubsPanelOpen, { enable: openCheckStubsPanel, disable: closeCheckStubsPanel }] =
    useToggle(false);
  const [
    isMatchRequirementsPanelOpen,
    { enable: openMatchRequirementsPanel, disable: closeMatchRequirementsPanel },
  ] = useToggle(false);
  const [
    isGovIdDetailsPanelOpen,
    { enable: openGovIdDetailsPanel, disable: closeGovIdDetailsPanel },
  ] = useToggle(false);
  const [isPresetsPanelOpen, { enable: openPresetsPanel, disable: closePresetsPanel }] =
    useToggle(false);

  const getEmptyStubs = (verificationTemplateVersion: VerificationTemplateVersion) => {
    if (verificationTemplateVersion.type === ApiModel.VerificationTemplateVersionGovernmentId) {
      return {
        'verification-stubs': {
          'check-stubs': [],
        },
        'document-stubs': {
          kind: 'government_id',
          extractions: {},
        },
      };
    } else {
      return {
        'verification-stubs': {
          'check-stubs': [],
        },
      };
    }
  };

  const setSelectedPresetAndStubs = useCallback(
    (preset: StubsWithOptionalId | null) => {
      setSelectedPreset(preset);
      // Set the associated stubs imperatively alongside the preset instead of in an
      // effect. This ensures that when stubs are rendered via the "Current Step" tab,
      // we do not inadvertently reset any one-off stubs
      if (verificationTemplateVersion.type in VERIFICATION_TYPE_TO_PRESETS) {
        setVerificationTemplateVersionStubsFieldPath(null, {
          id: verificationTemplateVersion.id,
          ...(preset?.['stubs'] || getEmptyStubs(verificationTemplateVersion)),
        });
      }
    },
    [setSelectedPreset, setVerificationTemplateVersionStubsFieldPath, verificationTemplateVersion],
  );

  const stubsEdited = useMemo(
    () =>
      selectedPreset &&
      !isEqual(selectedPreset.stubs, omit('id', verificationTemplateVersionStubs)),
    [selectedPreset, verificationTemplateVersionStubs],
  );

  const verificationTemplateVersionType = verificationTemplateVersion.type;
  const verificationTemplateVersionId = verificationTemplateVersion.id;
  const verificationStubs = verificationTemplateVersionStubs['verification-stubs'];

  let statusCheckStubs: CheckStub[] | undefined;
  let matchRequirementCheckStubs: MatchRequirementsCheckStub[] | undefined;
  let governmentIdExtractionStubs: GovernmentIdExtractions | undefined;
  let verificationStatusStub: SandboxForcedStatus | undefined;

  const hasMatchRequirementChecks = verificationTemplateVersion.attributes[
    'verification-checks'
  ].some((check) => MATCH_REQUIREMENT_CHECK_NAMES.includes(check.check));

  let checkStubs;
  if ('check-stubs' in verificationStubs) {
    checkStubs = verificationStubs['check-stubs'];
    statusCheckStubs = checkStubs.filter((checkStub) => 'status' in checkStub) as CheckStub[];
    matchRequirementCheckStubs = checkStubs.filter(
      (checkStub) => 'match-requirement-stubs' in checkStub,
    ) as MatchRequirementsCheckStub[];
  }
  if ('document-stubs' in verificationTemplateVersionStubs) {
    governmentIdExtractionStubs =
      verificationTemplateVersionStubs['document-stubs']?.['extractions'];
  }
  if ('status-stub' in verificationStubs) {
    verificationStatusStub = verificationStubs['status-stub'];
  }

  // console.log('checkStubs', checkStubs);

  return (
    <>
      <ModalWrapper isOpen={hasOpenSaveAsPresetModal} handleCloseModal={hideSaveAsPresetModal}>
        <SaveAsPresetModal
          presetType={ApiModel.SimulatePresetVerification}
          existingPresets={customPresets}
          hideModal={hideSaveAsPresetModal}
          onSave={async ({ name, description }) => {
            const createdPreset = await handleCreatePreset({
              name,
              description,
              'preset-type': ApiModel.SimulatePresetVerification,
              'verification-type': vtvApiTypePresetToVerificationType(
                verificationTemplateVersion.type,
              ),
              stubs: omit('id', verificationTemplateVersionStubs),
            });
            setSelectedPreset({
              ...createdPreset.attributes,
              id: createdPreset.id,
            });
            hideSaveAsPresetModal();
          }}
        />
      </ModalWrapper>
      <ModalWrapper isOpen={hasOpenUpdatePresetModal} handleCloseModal={hideUpdatePresetModal}>
        <UpdatePresetModal
          currentPresetName={selectedPreset?.name as string}
          currentPresetDescription={selectedPreset?.description as string}
          presetType={ApiModel.SimulatePresetVerification}
          existingPresets={customPresets}
          hideModal={hideUpdatePresetModal}
          onSave={async ({ name, description }) => {
            const updatedPreset = await handleUpdatePreset(
              // Cannot open this modal if `selectedPreset` is null
              selectedPreset?.id as string,
              ApiModel.SimulatePresetVerification,
              {
                name,
                description,
                stubs: omit('id', verificationTemplateVersionStubs),
              },
            );
            setSelectedPreset({
              ...updatedPreset.attributes,
              id: updatedPreset.id,
            });
            hideUpdatePresetModal();
          }}
        />
      </ModalWrapper>
      {verificationTemplateVersion.type in VERIFICATION_TYPE_TO_PRESETS && (
        <>
          <MediumHeaderContainer>
            <PresetHeaderContainer>
              <Label size='lg'>Preset</Label>
              {stubsEdited && <Sticker variant='caution'>Edited</Sticker>}
            </PresetHeaderContainer>
            <DropdownMenu
              trigger={
                <Button
                  id='save-dropdown__button'
                  size='sm'
                  variant='basic'
                  width='auto'
                  disabled={!stubsEdited}
                  suffixIcon={<CaretDownIcon />}
                >
                  Save
                </Button>
              }
              // Ensures that when the button trigger is disabled, clicking this button
              // does not trigger the dropdown
              triggerProps={{ disabled: !stubsEdited }}
            >
              <DropdownMenu.Item
                id='save-dropdown__update'
                onClick={showUpdatePresetModal}
                disabled={!selectedPreset?.id}
              >
                Update
              </DropdownMenu.Item>
              <DropdownMenu.Item id='save-dropdown__create-new' onClick={showSaveAsPresetModal}>
                Create New
              </DropdownMenu.Item>
            </DropdownMenu>
          </MediumHeaderContainer>
          <Spacer height={1} />
          <>
            <PresetCard
              aria-label='Selected Preset'
              onClick={openPresetsPanel}
              selected={selectedPreset != null}
            >
              <PresetCardFlexGroup>
                {selectedPreset && (
                  <div>
                    <ListIcon />
                  </div>
                )}
                {selectedPreset ? (
                  <div>
                    <Label>{selectedPreset.name}</Label>
                    <PresetCardDescription>{selectedPreset.description}</PresetCardDescription>
                  </div>
                ) : (
                  <div>
                    <Paragraph>No Preset selected</Paragraph>
                  </div>
                )}
              </PresetCardFlexGroup>
              {selectedPreset && (
                <Button
                  aria-label='Clear Preset'
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPresetAndStubs(null);
                  }}
                  width='icon'
                  variant='inverse'
                >
                  <XIcon />
                </Button>
              )}
            </PresetCard>
            <Spacer height={2} />
          </>
        </>
      )}
      {/*If any of the stubbing panels are open, render a div that overlays the element specified by */}
      {/*the panelOverlayId via a portal. When this overlay is clicked, we close the open panel. */}
      {/*This is a workaround that addresses the limitation of a parent container not being able to */}
      {/*listen to the click on a child (e.g. IFrame in cross-origin situation)*/}
      {(isPresetsPanelOpen ||
        isCheckStubsPanelOpen ||
        isMatchRequirementsPanelOpen ||
        isGovIdDetailsPanelOpen) &&
        panelOverlayId &&
        createPortal(
          <IframeOverlay
            onClick={() => {
              closePresetsPanel();
              closeCheckStubsPanel();
              closeMatchRequirementsPanel();
              closeGovIdDetailsPanel();
            }}
          />,
          document.getElementById(panelOverlayId) as Element,
        )}
      {isPresetsPanelOpen &&
        portaledPanel(
          <VerificationPresetsPanel
            verificationTemplateVersion={verificationTemplateVersion}
            selectedPreset={selectedPreset}
            setSelectedPreset={setSelectedPresetAndStubs}
            removeCustomPreset={removeCustomPreset}
            examplePresets={VERIFICATION_TYPE_TO_PRESETS[verificationTemplateVersion.type] || []}
            customPresets={customPresets.filter(
              (customPreset) =>
                customPreset.attributes['verification-type'] ===
                vtvApiTypePresetToVerificationType(verificationTemplateVersion.type),
            )}
            loadingCustomPresets={loadingSimulatePresets}
            reloadPresets={reloadPresets}
            offsetElementId={offsetElementId}
            onOutsideClick={closePresetsPanel}
            trackEvent={trackEvent}
          />,
          panelPortalId,
        )}
      {statusCheckStubs && checkStubs && (
        <>
          {isCheckStubsPanelOpen &&
            portaledPanel(
              <CheckStubsPanel
                verificationTemplateVersion={verificationTemplateVersion}
                checkStubs={checkStubs}
                setCheckStubs={(checkStubs) => {
                  setVerificationTemplateVersionStubsFieldPath(
                    'verification-stubs.check-stubs',
                    checkStubs,
                  );
                }}
                onOutsideClick={closeCheckStubsPanel}
                offsetElementId={offsetElementId}
                trackEvent={trackEvent}
              />,
              panelPortalId,
            )}
          <VerificationChecksTable
            checkStubs={statusCheckStubs}
            rightElement={
              <StubButtonsContainer>
                <Button
                  id='verification-checks-table__clear-stubs-button'
                  aria-label='Clear Verification Checks'
                  width='auto'
                  variant='inverse'
                  onClick={() => {
                    if ('check-stubs' in verificationStubs) {
                      const currentCheckStubs = verificationStubs['check-stubs'];
                      // Remove all status check stubs. The match requirement check stubs should still remain.
                      const newCheckStubs = currentCheckStubs.filter((stub) => !('status' in stub));
                      setVerificationTemplateVersionStubsFieldPath(
                        'verification-stubs.check-stubs',
                        newCheckStubs,
                      );
                    }
                  }}
                >
                  <TrashIcon />
                </Button>
                <Button
                  id='verification-checks-table__edit-stubs-button'
                  aria-label='Edit Verification Checks'
                  width='auto'
                  variant='inverse'
                  onClick={openCheckStubsPanel}
                >
                  <EditBoxIcon />
                </Button>
              </StubButtonsContainer>
            }
          />
        </>
      )}
      {checkStubs && matchRequirementCheckStubs && hasMatchRequirementChecks && (
        <>
          <Spacer height={2} />
          {isMatchRequirementsPanelOpen &&
            portaledPanel(
              <MatchRequirementCheckStubsPanel
                verificationTemplateVersion={verificationTemplateVersion}
                checkStubs={checkStubs}
                setCheckStubs={(checkStubs) => {
                  setVerificationTemplateVersionStubsFieldPath(
                    'verification-stubs.check-stubs',
                    checkStubs,
                  );
                }}
                onOutsideClick={closeMatchRequirementsPanel}
                offsetElementId={offsetElementId}
                trackEvent={trackEvent}
              />,
              panelPortalId,
            )}
          <VerificationMatchRequirementChecksTable
            verificationTemplateVersion={verificationTemplateVersion}
            matchRequirementCheckStubs={matchRequirementCheckStubs}
            rightElement={
              <StubButtonsContainer>
                <Button
                  id='verification-match-requirements-checks-table__clear-stubs-button'
                  aria-label='Clear Verification Checks with Match Requirements'
                  width='auto'
                  variant='inverse'
                  onClick={() => {
                    if ('check-stubs' in verificationStubs) {
                      const currentCheckStubs = verificationStubs['check-stubs'];
                      // Remove all match requirement check stubs. The status check stubs should still remain.
                      const newCheckStubs = currentCheckStubs.filter(
                        (stub) => !('match-requirement-stubs' in stub),
                      );
                      setVerificationTemplateVersionStubsFieldPath(
                        'verification-stubs.check-stubs',
                        newCheckStubs,
                      );
                    }
                  }}
                >
                  <TrashIcon />
                </Button>
                <Button
                  id='verification-match-requirements-checks-table__edit-stubs-button'
                  aria-label='Edit Verification Checks with Match Requirements'
                  width='auto'
                  variant='inverse'
                  onClick={openMatchRequirementsPanel}
                >
                  <EditBoxIcon />
                </Button>
              </StubButtonsContainer>
            }
          />
        </>
      )}
      {governmentIdExtractionStubs && (
        <>
          <Spacer height={2} />
          {isGovIdDetailsPanelOpen &&
            portaledPanel(
              <GovernmentIdDocumentStubsPanel
                governmentIdExtractionStubs={governmentIdExtractionStubs}
                setGovernmentIdExtractionStubs={({ key, value }) => {
                  setVerificationTemplateVersionStubsFieldPath('document-stubs', {
                    kind: 'government_id',
                    extractions: {
                      ...governmentIdExtractionStubs,
                      [key]: value,
                    },
                  });
                }}
                onOutsideClick={closeGovIdDetailsPanel}
                offsetElementId={offsetElementId}
                trackEvent={trackEvent}
              />,
              panelPortalId,
            )}
          <GovernmentIdDetailsTable
            extractionStubs={governmentIdExtractionStubs}
            rightElement={
              <StubButtonsContainer>
                <Button
                  id='government-id-details-table__clear-stubs-button'
                  aria-label='Clear ID Details'
                  width='auto'
                  variant='inverse'
                  onClick={() =>
                    setVerificationTemplateVersionStubsFieldPath('document-stubs.extractions', {})
                  }
                >
                  <TrashIcon />
                </Button>
                <Button
                  id='government-id-details-table__edit-stubs-button'
                  aria-label='Edit ID Details'
                  width='auto'
                  variant='inverse'
                  onClick={openGovIdDetailsPanel}
                >
                  <EditBoxIcon />
                </Button>
              </StubButtonsContainer>
            }
          />
        </>
      )}
      {verificationStatusStub && (
        <ToggleSlider
          id={`legacy-sandbox-toggle-${verificationTemplateVersionId}`}
          labelOn='Pass'
          labelOff='Fail'
          value={verificationStatusStub === SandboxForcedStatus.Passed}
          onChange={(e) => {
            setVerificationTemplateVersionStubsFieldPath(
              'verification-stubs.status-stub',
              e ? SandboxForcedStatus.Passed : SandboxForcedStatus.Failed,
            );
            trackEvent('Flow Editor - Preview Verification Pass/Fail Toggled', {
              verification_type: verificationTemplateVersionType,
            });
          }}
        />
      )}
    </>
  );
};

export default VerificationTemplateVersionStubsConfig;
